import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Camera, Loader2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';

export function PanelDeLogotipo({ tieneLogo }: { tieneLogo: boolean }) {
  const clienteDeConsultas = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const logotipo = useQuery({
    queryKey: ['emisor', 'logotipo'],
    queryFn: async () => {
      const imagen = await api.descargar('/emisor/logo');
      return imagen ? aUrlDeDatos(imagen) : null;
    },
    enabled: tieneLogo,
  });

  const cargar = useMutation({
    mutationFn: (archivo: File) => {
      const formulario = new FormData();
      formulario.append('archivo', archivo);
      return api.subir<{ tipo: string }>('/emisor/logo', formulario);
    },
    onSuccess: () => {
      setError(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['emisor'] });
    },
    onError: (e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el logotipo'),
  });

  const cargando = cargar.isPending || logotipo.isFetching;

  return (
    <div className="rounded-2xl border border-armazon-100 bg-armazon-50 p-6 text-center">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-armazon-600">
        Logo de la empresa
      </p>

      <div className="relative inline-block">
        <div
          className={[
            'mx-auto grid size-32 place-items-center overflow-hidden rounded-2xl border-2 border-dashed bg-white',
            logotipo.data ? 'border-marca-200' : 'border-armazon-200',
          ].join(' ')}
        >
          {logotipo.data ? (
            <img
              src={logotipo.data}
              alt="Logotipo de la empresa"
              className="size-full object-contain p-2"
            />
          ) : (
            <div className="text-center">
              <Building2 className="mx-auto mb-1 size-8 text-armazon-300" aria-hidden />
              <p className="text-[10px] uppercase text-armazon-500">Sin logo</p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => entrada.current?.click()}
          disabled={cargando}
          title="Cambiar el logotipo"
          aria-label="Cambiar el logotipo"
          className="absolute -bottom-2 -right-2 grid size-9 place-items-center rounded-xl bg-marca-600 text-white shadow-lg transition-colors hover:bg-marca-700 disabled:opacity-60"
        >
          {cargando ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Camera className="size-4" aria-hidden />
          )}
        </button>

        <input
          ref={entrada}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) {
              cargar.mutate(archivo);
            }

            e.target.value = '';
          }}
        />
      </div>

      <p className="mt-4 text-xs uppercase text-armazon-600">PNG, JPG o WebP · máx. 2 MB</p>

      <p className="mt-1 text-[11px] uppercase leading-relaxed text-armazon-400">
        En PNG o JPG sale también en los reportes en PDF. El WebP solo se ve en pantalla.
      </p>

      {error && (
        <p role="alert" className="mt-2 text-xs text-alerta">
          {error}
        </p>
      )}
    </div>
  );
}

function aUrlDeDatos(imagen: Blob): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result));
    lector.onerror = () => rechazar(new Error('No se pudo leer la imagen'));
    lector.readAsDataURL(imagen);
  });
}
